/** The logo step: the logo in use, the candidates the logo search found on the company's website, and
 *  an upload. Candidates are fetched with the session token and shown as `blob:` URLs (the
 *  dashboard's CSP refuses the storage URL). A choice or upload answers the new logo state, which
 *  the session's tour operator takes at once (the sidebar logo and the app icon follow). */
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, Button, ButtonBase, CircularProgress, Stack, Typography } from '@mui/material';
import * as React from 'react';

import { imageURL } from '../components/images';
import { ContextStore } from '../store/contextStore';
import { OnboardingApiError } from './api';
import type { OnboardingApi } from './api';
import { ErrorLine, Section } from './components';
import { LOGO_STATUSES } from './labels';
import type { OnboardingLaneState, OnboardingLogo, OnboardingLogoCandidate } from './types';

/** The upload limit the backend applies (15 MB); a bigger file is refused here first. */
export const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
export const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface Props {
    api: OnboardingApi;
    canWrite: boolean;
    /** The logo lane's state: the card re-reads when it changes (the search finished, ops chose). */
    laneState?: OnboardingLaneState | string;
    onChanged?: () => void;
}

export default function LogoCard({ api, canWrite, laneState, onChanged }: Props) {
    const { settings, updateTourOperator } = React.useContext(ContextStore) as any;
    const [logo, setLogo] = React.useState<OnboardingLogo | null>(null);
    const [loadError, setLoadError] = React.useState<unknown>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [picking, setPicking] = React.useState(false);
    const fileInput = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        const ctrl = new AbortController();
        api.logo(ctrl.signal).then(
            (l) => {
                if (!ctrl.signal.aborted) {
                    setLogo(l);
                    setLoadError(null);
                }
            },
            (e) => {
                if (!ctrl.signal.aborted && e?.name !== 'AbortError') {
                    setLoadError(e);
                }
            },
        );
        return () => ctrl.abort();
    }, [api, laneState]);

    const change = async (action: () => Promise<OnboardingLogo>) => {
        setBusy(true);
        setError(null);
        try {
            const next = await action();
            setLogo(next);
            setPicking(false);
            updateTourOperator?.((top: any) => ({ ...top, logo: next.logo }));
            onChanged?.();
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    const onFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files && ev.target.files[0];
        ev.target.value = '';
        if (!file) {
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            // Refused here first, with the backend's own code (it would answer the same).
            setError(new OnboardingApiError(400, { code: 'image_too_large' }));
            return;
        }
        change(() => api.uploadLogo(file));
    };

    if (loadError && !logo) {
        return (
            <Section title="Logo" testId="logo-card">
                <ErrorLine error={loadError} />
            </Section>
        );
    }
    if (!logo) {
        return (
            <Section title="Logo" testId="logo-card">
                <CircularProgress size={20} />
            </Section>
        );
    }

    const currentUrl = logo.logo ? imageURL(settings?.images?.base, logo.logo, 'x192') : null;
    const needsChoice = logo.status === 'needs_choice' || logo.low_confidence;
    const showCandidates = logo.candidates.length > 0 && (needsChoice || picking);
    const statusWords = logo.low_confidence && logo.status === 'auto'
        ? "We picked this logo from your website, but we're not sure it's right. Check it, choose another one, or upload your logo."
        : LOGO_STATUSES[logo.status] || logo.status;

    return (
        <Section title="Logo" testId="logo-card">
            <Stack spacing={1.5}>
                {currentUrl ? (
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center', bgcolor: 'grey.50' }}>
                        <img src={currentUrl} alt="Your logo" style={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }} data-testid="current-logo" />
                    </Box>
                ) : null}
                <Typography variant="body2" color={needsChoice ? 'text.primary' : 'text.secondary'} data-testid="logo-status">
                    {logo.status === 'running' && <CircularProgress size={12} sx={{ mr: 1 }} />}
                    {statusWords}
                </Typography>

                {showCandidates && (
                    <CandidateGrid
                        api={api}
                        candidates={logo.candidates}
                        selected={logo.selected}
                        disabled={!canWrite || busy}
                        onPick={(index) => change(() => api.chooseLogo({ choice: 'candidate', candidate: index }))}
                    />
                )}

                <ErrorLine error={error} testId="logo-error" />

                {canWrite && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadOutlinedIcon />}
                            disabled={busy}
                            onClick={() => fileInput.current?.click()}
                        >
                            Upload logo
                        </Button>
                        {logo.candidates.length > 0 && !showCandidates && (
                            <Button size="small" disabled={busy} onClick={() => setPicking(true)}>Choose a found logo</Button>
                        )}
                        {logo.candidates.length > 0 && logo.status !== 'declined' && (showCandidates || logo.choice === 'candidate' || logo.status === 'auto') && (
                            <Button size="small" disabled={busy} onClick={() => change(() => api.chooseLogo({ choice: 'none' }))}>
                                Don&apos;t use a found logo
                            </Button>
                        )}
                        <input
                            ref={fileInput}
                            type="file"
                            accept={ACCEPTED_TYPES.join(',')}
                            hidden
                            data-testid="logo-file"
                            onChange={onFile}
                        />
                    </Stack>
                )}
            </Stack>
        </Section>
    );
}

function CandidateGrid({ api, candidates, selected, disabled, onPick }: {
    api: OnboardingApi;
    candidates: OnboardingLogoCandidate[];
    selected: number | null;
    disabled: boolean;
    onPick: (index: number) => void;
}) {
    return (
        <Box
            role="radiogroup"
            aria-label="Logos found on your website"
            sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1 }}
        >
            {candidates.map((c) => (
                <CandidateTile key={c.index} api={api} candidate={c} selected={selected === c.index} disabled={disabled} onPick={() => onPick(c.index)} />
            ))}
        </Box>
    );
}

function CandidateTile({ api, candidate, selected, disabled, onPick }: {
    api: OnboardingApi;
    candidate: OnboardingLogoCandidate;
    selected: boolean;
    disabled: boolean;
    onPick: () => void;
}) {
    const url = useCandidateImage(api, candidate.index);
    const size = candidate.width && candidate.height ? `${candidate.width}×${candidate.height}` : null;
    return (
        <ButtonBase
            role="radio"
            aria-checked={selected}
            aria-label={`Logo ${candidate.index + 1}${size ? `, ${size}` : ''}`}
            disabled={disabled}
            onClick={onPick}
            data-testid={`logo-candidate-${candidate.index}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                border: selected ? 2 : 1,
                borderColor: selected ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 1,
                minHeight: 72,
                bgcolor: 'grey.50',
            }}
        >
            {url === null ? (
                <CircularProgress size={16} />
            ) : url ? (
                <img src={url} alt="" style={{ maxHeight: 48, maxWidth: '100%', objectFit: 'contain' }} />
            ) : (
                <Typography variant="caption" color="text.secondary">No preview</Typography>
            )}
            {size && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>{size}</Typography>}
        </ButtonBase>
    );
}

/** A candidate's image as a `blob:` URL (null while loading, '' when it could not be read),
 *  revoked when the tile goes away. */
function useCandidateImage(api: OnboardingApi, index: number): string | null {
    const [url, setUrl] = React.useState<string | null>(null);
    React.useEffect(() => {
        const ctrl = new AbortController();
        let objectUrl: string | null = null;
        api.candidateImage(index, ctrl.signal).then(
            (blob) => {
                if (ctrl.signal.aborted) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            },
            () => {
                if (!ctrl.signal.aborted) {
                    setUrl('');
                }
            },
        );
        return () => {
            ctrl.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [api, index]);
    return url;
}
